/**
 * Экран управления общим пулом энергии
 * Позволяет выбирать существо и тратить энергию из пула
 */

import { logger } from "../../utils/logger.js";

export function createEnergyManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  getCurrentUser,
  setCurrentUser,
  updateUserBalancesUI,
  getUserCollection,
  documentRef = document
}) {
  
  /**
   * Загружает пул энергии
   */
  async function loadEnergyPool() {
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/energy/pool`, {
        method: 'GET',
        headers: buildAuthHeaders()
      });

      // safeFetch оборачивает ответ, проверяем вложенную структуру
      if (response.success && response.data) {
        if (response.data.data) {
          return response.data.data.energy_pool || 0;
        } else if (response.data.success && response.data.data) {
          return response.data.data.energy_pool || 0;
        } else if (response.data.success) {
          return response.data.energy_pool || 0;
        } else {
          return response.data.energy_pool || 0;
        }
      }
      
      return 0;
    } catch (error) {
      logger.error('[EnergyManager] Ошибка загрузки пула энергии:', error);
      return 0;
    }
  }

  /**
   * Применяет энергию к существу
   */
  async function applyEnergyToCreature(creatureId, amount) {
    const telegramWebApp = getTelegramWebApp();
    
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/energy/apply`, {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ creatureId, amount })
      });

      // safeFetch оборачивает ответ, проверяем вложенную структуру
      let data = null;
      if (response.success && response.data) {
        if (response.data.data) {
          data = response.data.data;
        } else if (response.data.success && response.data.data) {
          data = response.data.data;
        } else {
          data = response.data;
        }
      }
      
      if (response.success && data) {
        // Обновляем баланс пользователя
        const currentUser = getCurrentUser();
        if (currentUser && data.user) {
          const updatedUser = { ...currentUser, ...data.user };
          setCurrentUser(updatedUser);
          updateUserBalancesUI(updatedUser);
        }
        
        // Показываем кастомное уведомление
        const lootboxManager = typeof window !== 'undefined' ? window.lootboxManager : null;
        if (lootboxManager && lootboxManager.showLootboxNotification) {
          lootboxManager.showLootboxNotification(
            '⚡ Энергия добавлена', 
            `+${data.energyAdded || amount} энергии\nНовая энергия: ${data.newEnergy || 0}/100`
          );
        } else if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(`✅ Добавлено ${data.energyAdded || amount} энергии существу!\nНовая энергия: ${data.newEnergy || 0}/100`);
        }
        
        return true;
      } else {
        const errorMessage = response.data?.message || response.error || 'Ошибка применения энергии';
        const lootboxManager = typeof window !== 'undefined' ? window.lootboxManager : null;
        if (lootboxManager && lootboxManager.showLootboxNotification) {
          lootboxManager.showLootboxNotification('❌ Ошибка', errorMessage);
        } else if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(errorMessage);
        }
        return false;
      }
    } catch (error) {
      logger.error('[EnergyManager] Ошибка применения энергии:', error);
      const lootboxManager = typeof window !== 'undefined' ? window.lootboxManager : null;
      if (lootboxManager && lootboxManager.showLootboxNotification) {
        lootboxManager.showLootboxNotification('❌ Ошибка', 'Ошибка применения энергии. Попробуйте позже.');
      } else if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert('Ошибка применения энергии. Попробуйте позже.');
      }
      return false;
    }
  }

  /**
   * Рендерит экран управления энергией
   */
  async function renderEnergyManager() {
    const domCache = window.__domCacheManager;
    const container = domCache?.getCachedElement('#energy-manager-container', documentRef) 
      || documentRef.getElementById('energy-manager-container');
    
    if (!container) {
      logger.warn('[EnergyManager] Контейнер не найден');
      return;
    }

    logger.debug('[EnergyManager] Загрузка пула энергии и коллекции...');
    const energyPool = await loadEnergyPool();
    logger.debug('[EnergyManager] Пул энергии:', energyPool);
    
    const collection = await getUserCollection();
    logger.debug('[EnergyManager] Коллекция:', collection?.length || 0, 'существ');
    
    if (!collection || collection.length === 0) {
      container.innerHTML = `
        <div style="padding: 40px; text-align: center; color: var(--tg-theme-hint-color);">
          <div style="font-size: 48px; margin-bottom: 16px;">⚡</div>
          <div style="font-size: 18px; margin-bottom: 8px;">Нет существ</div>
          <div style="font-size: 14px;">Добавьте существ в коллекцию, чтобы тратить энергию</div>
        </div>
      `;
      return;
    }

    let html = `
      <div style="padding: 16px;">
        <div style="
          background: var(--tg-theme-bg-color, #1a1a2e);
          border: 2px solid var(--tg-theme-button-color, #4dfffd);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
          text-align: center;
        ">
          <div style="font-size: 24px; margin-bottom: 8px;">⚡ Общий пул энергии</div>
          <div style="font-size: 32px; font-weight: 600; color: var(--tg-theme-button-color, #4dfffd);">
            ${energyPool}
          </div>
          <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 8px;">
            Выберите существо и количество энергии для применения
          </div>
        </div>
        
        <!-- Информационный блок о том, зачем нужна энергия -->
        <div style="
          background: linear-gradient(135deg, rgba(77, 255, 253, 0.1) 0%, rgba(77, 255, 253, 0.05) 100%);
          border: 1px solid rgba(77, 255, 253, 0.3);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
        ">
          <div style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--tg-theme-button-color, #4dfffd);">
            💡 Зачем нужна энергия?
          </div>
          <div style="font-size: 14px; line-height: 1.6; color: var(--tg-theme-text-color, #ffffff); margin-bottom: 12px;">
            Энергия нужна для участия в боях и улучшения существ:
          </div>
          <div style="display: grid; gap: 8px; margin-bottom: 12px;">
            <div style="display: flex; align-items: start; gap: 8px;">
              <div style="font-size: 20px; flex-shrink: 0;">⚔️</div>
              <div style="flex: 1;">
                <div style="font-weight: 600; font-size: 13px; margin-bottom: 4px;">В битвах:</div>
                <div style="font-size: 12px; color: var(--tg-theme-hint-color, rgba(255,255,255,0.7));">
                  • Базовая атака: <span style="color: #4dfffd;">10 энергии</span><br>
                  • Способности: <span style="color: #4dfffd;">25 энергии</span><br>
                  • Ультимативная способность: <span style="color: #4dfffd;">50 энергии</span>
                </div>
              </div>
            </div>
            <div style="display: flex; align-items: start; gap: 8px;">
              <div style="font-size: 20px; flex-shrink: 0;">📈</div>
              <div style="flex: 1;">
                <div style="font-weight: 600; font-size: 13px; margin-bottom: 4px;">При улучшении:</div>
                <div style="font-size: 12px; color: var(--tg-theme-hint-color, rgba(255,255,255,0.7));">
                  Некоторые методы улучшения характеристик требуют энергию
                </div>
              </div>
            </div>
            <div style="display: flex; align-items: start; gap: 8px;">
              <div style="font-size: 20px; flex-shrink: 0;">🔄</div>
              <div style="flex: 1;">
                <div style="font-weight: 600; font-size: 13px; margin-bottom: 4px;">Восстановление:</div>
                <div style="font-size: 12px; color: var(--tg-theme-hint-color, rgba(255,255,255,0.7));">
                  Энергия восстанавливается автоматически: <span style="color: #4dfffd;">+1 каждые 10 минут</span>
                </div>
              </div>
            </div>
          </div>
          <div style="
            background: rgba(77, 255, 253, 0.1);
            border-radius: 8px;
            padding: 12px;
            margin-top: 12px;
            border: 1px solid rgba(77, 255, 253, 0.2);
          ">
            <div style="font-size: 12px; color: var(--tg-theme-hint-color, rgba(255,255,255,0.7)); margin-bottom: 8px;">
              <strong style="color: var(--tg-theme-text-color, #ffffff);">Как получить энергию:</strong>
            </div>
            <div style="font-size: 12px; color: var(--tg-theme-hint-color, rgba(255,255,255,0.7));">
              • Купить в <span style="color: #4dfffd; cursor: pointer;" onclick="showScreen('store'); setTimeout(() => { const resourcesTab = document.querySelector('[onclick*=\"resources\"]'); if (resourcesTab) resourcesTab.click(); }, 100);">магазине</span> (вкладка "Ресурсы")<br>
              • Получить из <span style="color: #4dfffd; cursor: pointer;" onclick="showScreen('store'); setTimeout(() => { const boxesTab = document.querySelector('[onclick*=\"boxes\"]'); if (boxesTab) boxesTab.click(); }, 100);">лутбоксов</span><br>
              • Автоматическое восстановление (+1 каждые 10 минут)
            </div>
          </div>
        </div>
        
        <div style="display: grid; gap: 12px;">
    `;
    
    collection.forEach(creature => {
      const currentEnergy = creature.energy || 0;
      const maxEnergy = 100;
      const energyPercent = Math.floor((currentEnergy / maxEnergy) * 100);
      const canAdd = energyPool > 0 && currentEnergy < maxEnergy;
      const maxCanAdd = Math.min(energyPool, maxEnergy - currentEnergy);
      
      html += `
        <div style="
          background: var(--tg-theme-bg-color, #1a1a2e);
          border: 1px solid var(--tg-theme-hint-color, rgba(255,255,255,0.2));
          border-radius: 8px;
          padding: 12px;
        ">
          <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 8px;">
            <div style="font-size: 32px;">${creature.visual || '🦉'}</div>
            <div style="flex: 1;">
              <div style="font-weight: 600; font-size: 14px;">${creature.name || 'Существо'} ${creature.creatureId ? `#${creature.creatureId}` : creature.id ? `#${creature.id}` : ''}</div>
              <div style="font-size: 12px; color: var(--tg-theme-hint-color);">
                Уровень ${creature.level || 1}
              </div>
            </div>
          </div>
          
          <div style="margin-bottom: 8px;">
            <div style="display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 4px;">
              <span>Энергия</span>
              <span>${currentEnergy}/${maxEnergy} (${energyPercent}%)</span>
            </div>
            <div style="
              width: 100%;
              height: 8px;
              background: var(--tg-theme-hint-color, rgba(255,255,255,0.1));
              border-radius: 4px;
              overflow: hidden;
            ">
              <div style="
                width: ${energyPercent}%;
                height: 100%;
                background: ${energyPercent > 50 ? '#4dff4d' : energyPercent > 25 ? '#ffcc00' : '#ff4d4d'};
                transition: width 0.3s;
              "></div>
            </div>
          </div>
          
          ${canAdd ? `
            <div style="display: flex; gap: 8px;">
              <button onclick="window.applyEnergyToCreature(${creature.creatureId || creature.id}, 10)" style="
                flex: 1;
                background: var(--tg-theme-button-color, #4dfffd);
                color: var(--tg-theme-button-text-color, #000);
                border: none;
                border-radius: 6px;
                padding: 8px;
                font-weight: 600;
                font-size: 12px;
                cursor: pointer;
                transition: opacity 0.2s;
              " onmouseover="this.style.opacity='0.8'" onmouseout="this.style.opacity='1'">+10</button>
              <button onclick="window.applyEnergyToCreature(${creature.creatureId || creature.id}, ${maxCanAdd})" style="
                flex: 1;
                background: var(--tg-theme-button-color, #4dfffd);
                color: var(--tg-theme-button-text-color, #000);
                border: none;
                border-radius: 6px;
                padding: 8px;
                font-weight: 600;
                font-size: 12px;
                cursor: pointer;
                transition: opacity 0.2s;
              " onmouseover="this.style.opacity='0.8'" onmouseout="this.style.opacity='1'">+${maxCanAdd} (макс)</button>
            </div>
          ` : `
            <div style="
              text-align: center;
              padding: 10px;
              background: var(--tg-theme-hint-color, rgba(255,255,255,0.05));
              border-radius: 6px;
              font-size: 12px;
              color: var(--tg-theme-hint-color);
              border: 1px solid var(--tg-theme-hint-color, rgba(255,255,255,0.1));
            ">
              ${currentEnergy >= maxEnergy 
                ? '✅ Энергия максимальна (100/100)' 
                : energyPool === 0 
                  ? '⚠️ В пуле нет энергии. Купите энергию в магазине или получите из лутбоксов'
                  : 'Недостаточно энергии в пуле'}
            </div>
          `}
        </div>
      `;
    });
    
    html += `
        </div>
      </div>
    `;
    
    // Используем domCache для оптимизации обновления (уже объявлен выше)
    if (domCache) {
      domCache.updateHTML('#energy-manager-container', html, documentRef);
    } else {
      container.innerHTML = html;
    }
  }

  return {
    loadEnergyPool,
    applyEnergyToCreature,
    renderEnergyManager
  };
}

